<script setup lang="ts">
import {
  computed,
  defineOgImageComponent,
  ref,
  useAsyncData,
  useAuth,
  useDialog,
  useHead,
  useMaps,
  watch
} from '#imports';
const { fetchFeaturedMaps } = useMaps();

const { data: featuredMaps } = await useAsyncData(
  'featuredMaps',
  fetchFeaturedMaps,
);

import { useWindowScroll } from '@vueuse/core'
const { headerStyle, setHeaderStyle } = useAuth();
setHeaderStyle('transparent')
const { y } = useWindowScroll()
watch(y, () => {
  if (y.value > 50) {
    setHeaderStyle('opaque')
  } else {
    setHeaderStyle('transparent')
  }
})

const { openDialog } = useDialog();

const creatorOnboarding = () => {
  openDialog('MPAuth', {
    title: 'Sign up & Apply',
    subtitle: 'Apply to become a Mappr Creator',
    startOn: 'signup',
    userType: 'creator',
  });
};

const faqItems = [
  {
    id: '1',
    header: 'What is Mappr?',
    content:
      'Mappr is a way to support your favorite creators and have access to their knowledge of the world.',
  },
  {
    id: '2',
    header: 'How does Mappr work?',
    content:
      'We share revenue from subscriptions with our creators, helping to cultavate a rich commmunity of creators and Explorers.',
  },
  {
    id: '3',
    header: 'How can I get involved?',
    content:
      'You can join mappr as a creator or simply create an account to start saving spots, building your atlas and more.',
  },
];

useHead(() => ({
  title: 'Map your spots & grow your audience',
  meta: [
    {
      name: 'description',
      content:
        'Earn recurring revenue by sharing your curated lists and guides',
    },
  ],
}));

defineOgImageComponent('Default', {
  title: 'Map your spots & grow your audience',
  description:
    'Earn recurring revenue by sharing your curated lists and guides',
});

const spotRangeValue = ref(25);
const howManySupporterRangeValue = ref(30);
const supportCostRangeValue = ref(7);

// Placeholder calculation
const estimatedRevenue = computed(() => {
  const spotWeight = spotRangeValue.value * 0.01;
  const supporterCount = Math.round(
    howManySupporterRangeValue.value * spotWeight,
  );
  const supporterCost = supporterCount * supportCostRangeValue.value;
  return supporterCost;
});

const progressColor = 'var(--color-orange-500)';
</script>

<template>
  <div class="creators-page">
    <MPCreatorsHero />
    <section class="connecting-creators">
      <MPSectionHeader
        subtitle="How it works"
        title="Monetize your World Knowledge & Expertise"
        summary="By building a curated map, you’ll help your followers discover amazing places while generating ongoing income to support your passion"
        class="header"
      />
      <div class="steps-container">
        <div class="step-container">
          <header>
            <span class="step-number">
              1
            </span>
          </header>
          <main>
            <h3 class="create-a-map-title">
              Create a Map
            </h3>
            <p>
            Craft a personalized map with your expert recommendations.
            </p>
          </main>
        </div>
        <div class="step-container">
          <header>
            <span class="step-number">
              2
            </span>
          </header>
          <main>
            <h3>
              Publish
            </h3>
            <p>
              Share your map and let followers explore your curated spots.
            </p>
          </main>
        </div>
        <div class="step-container">
          <header>
            <span class="step-number">
              3
            </span>
          </header>
          <main>
            <h3>
            Earn
            </h3>
            <p>
              Generate income as your followers discover your recommendations.
            </p>
          </main>
        </div>
        <div class="step-container">
          <header>
            <span class="step-number">
              4
            </span>
          </header>
          <main>
            <h3>
              Update
            </h3>
            <p>
              Keep your map fresh by adding new spots and hidden gems.
            </p>
          </main>
        </div>
      </div>
    </section>
    <section class="how-much-earn">
      <header>
        <MPSectionHeader
          subtitle="Revenue forecast"
          title="Calculate your earning potential"
          summary="Maximize your earnings by expanding your spots, growing supporters, and setting the right price."
          class="header"
          :center-title="false"
        />
        <div class="desktop-onboard-button">
          <MPButton label="Get Started" version="primary" background-color="black" rounded @click="creatorOnboarding"/>&nbsp;
        </div>
      </header>

      <div class="slider-container">
        <header>
          <h4>Total Spots</h4>
          <p>How many valuable, hard-to-find places can you recommend?</p>
        </header>
        <MPRangeSlider
          v-model="spotRangeValue"
          :min="0"
          :max="100"
          :progress-color="progressColor"
          width="100%"
        />
      </div>
      <div class="slider-container">
        <header>
          <h4>Support Price</h4>
          <p>What’s the ideal price to get access to your recommendations?</p>
        </header>
        <MPRangeSlider
          v-model="supportCostRangeValue"
          :min="0"
          :max="30"
          :progress-color="progressColor"
          width="100%"
        />
      </div>
      <div class="slider-container">
        <header>
          <h4>Supporters Estimate</h4>
          <p>How many people would value and support your recommendations?</p>
        </header>
        <main>
        <MPRangeSlider
          v-model="howManySupporterRangeValue"
          :min="0"
          :max="1000"
          :progress-color="progressColor"
          width="100%"
        />
        </main>
      </div>
      <div class="earnings-estimate">
        <span class="title">Estimated Revenue</span>
        <br>
        <div class="earnings-container">
          <span class="currency">€</span>
          <span class="value">{{ estimatedRevenue }}</span>
          <span class="period"> /month </span><br>
        </div>
      </div>
      <small class="disclaimer">*The earnings estimate provided is for illustrative purposes only. It does not guarantee actual earnings and should not be considered a promise or a precise calculation. Results will vary based on performance</small>
      <div class="mobile-onboard-button">
       <MPButton label="Get Started" version="primary" background-color="black" rounded @click="creatorOnboarding"/>&nbsp;
      </div>
    </section>
    <section class="built-for-creators">
      <header>
        <MPSectionHeader
          subtitle="Features"
          title="Built for Creators"
          summary="Designed to empower creators with tools that help grow their audience, share their expertise, and elevate their content creation to a professional level."
          class="header"
          :center-title="false"
        />
      </header>
      <main>
        <div class="creator-card-container">
          <div class="for-creators-card">
            <div class="icon">
              <MPImg
                src="/media/images/for-creators-icon-question.svg"
                :width="50"
                :height="50"
              />
            </div>
            <div class="content">
              <h4>Map Creation</h4>
              <p>Create and customize unlimited maps with your picks.</p>
            </div>
          </div>
          <div class="for-creators-card">
            <div class="icon">
              <MPImg
                src="/media/images/for-creators-icon-smile.svg"
                :width="50"
                :height="50"
              />
            </div>
            <div class="content">
              <h4>Supporters</h4>
              <p>Create exclusive content and earn through
              subscriptions.</p>
            </div>
          </div>
          <div class="for-creators-card">
            <div class="icon">
              <MPImg
                src="/media/images/for-creators-icon-speaker.svg"
                :width="50"
                :height="50"
              />
            </div>
            <div class="content">
              <h4>Creator Studio</h4>
              <p>Manage maps, content, and pricing all in one powerful tool.</p>
            </div>
          </div>
          <div class="for-creators-card">
            <div class="icon">
              <MPImg
                src="/media/images/for-creators-icon-person.svg"
                :width="50"
                :height="50"
              />
            </div>
            <div class="content">
              <h4>Store</h4>
              <p>Sell digital products alongside maps to increase your revenue</p>
            </div>
          </div>
          <div class="for-creators-card">
            <div class="icon">
              <MPImg
                src="/media/images/for-creators-icon-antenna.svg"
                :width="50"
                :height="50"
              />
            </div>
            <div class="content">
              <h4>Referral Links</h4>
              <p>Add referral links to spots and earn commissions on bookings.</p>
            </div>
          </div>
          <div class="for-creators-card">
            <div class="icon">
              <MPImg
                src="/media/images/for-creators-icon-hand.svg"
                :width="50"
                :height="50"
              />
            </div>
            <div class="content">
              <h4>Revenue Share</h4>
              <p>Earn your share of the revenue generated by your content.</p>
            </div>
          </div>
        </div>
        <div class="skew-container">
          <MPSkewContainer
            id="outline-skew"
            stroke="var(--color-green-300)"
            class="outline"
          />
          <MPSkewContainer
            id="map-skew"
            fill="var(--color-green-100)"
            class="fill"
            animate
          />
        </div>
      </main>

    </section>
    <section v-if="featuredMaps.length > 0" class="made-with-mapper-carousel">
      <MPSectionHeader
        subtitle="By creators"
        title="Made with Mappr"
        summary="Explore maps created by our community, featuring carefully curated collections that resonate with their audiences and provide valuable insights for all."
        class="header"
      />
      <main>
        <MPCarousel
          :data="featuredMaps"
        />
      </main>
    </section>
    <section v-if="faqItems" class="creator-faq">
      <MPSectionHeader
        subtitle="Frequently Asked Questions"
        title="Frequently Asked Questions"
        summary="Get recommendations on spots, Travel info, offers and more from our community of creators & travel experts."
      />
      <MPAccordion
        :items="faqItems"
        class="faq-list"
      />
    </section>
  </div>
</template>

<style lang="postcss" scoped>
@import '@/assets/css/breakpoints.pcss';
@import '@/assets/css/typography.pcss';

.desktop-onboard-button {
  display: none;

  @mixin screen sm {
    display: block;
  }
}

.mobile-onboard-button {
  display: block;
  margin-block: 1rem;

  @mixin screen sm {
    display: none;
  }
}

.creators-page {
  position: relative;
  z-index: 0;
  top: -7.5rem;
  @mixin screen sm {
    all: unset;
    overflow-x: hidden;
    max-width: 100vw;
  }
}

.create-a-map-title {
  position: relative;
  z-index: 1
}

.create-a-map-title::before {
  content: "";
  position: absolute;
  background-color: var(--color-green-300);
  z-index: -1;
  height: 100%;
  width: 75%;

  @mixin screen sm {
    height: 80%;
    width: 60%;
  }

  @mixin screen lg {
    top: 0.5rem;
    left: -1rem;
    width: 120%;
    height: 50%;
    transform: rotate(-3deg);
  }

}

@define-mixin section-gap {
  padding: 4rem 1rem;
  @mixin screen sm {
    padding: 7rem 2rem;
  }
  @mixin screen xl {
    padding: 8rem 1rem;
  }
}

.connecting-creators {
  margin: 3rem auto 0;
  @mixin section-gap;
  text-align: center;
  display: grid;
  place-items: center;
  max-width: var(--default-content-width);

  .steps-container {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem;
    text-align: left;
    position: relative;
    z-index: 1;

    header {
      @mixin screen lg {
        display: flex;
        justify-content: center;
      }
    }

    &::before {
      z-index: -1;
      content: '';
      position: absolute;
      left: 2rem; /* Adjust based on your step number position */
      top: 6rem;
      height: calc(100% - 12rem);
      width: 3px;
      background-color: var(--color-gray-500);

      @mixin screen lg {
        left: 50%;
        top: 2rem;
        width: calc(100% - 16rem);
        height: 3px;
        transform: translateX(-50%);
        background-color: var(--color-gray-300);



      }
    }

    @mixin screen sm {
      max-width: 100%;
    }

    @mixin screen lg {
      grid-template-columns: repeat(4, 1fr);
      margin-top: 5rem;
    }
    @mixin screen xl {
      grid-template-columns: repeat(4, 1fr);
    }

    .step-container {
      display: flex;
      max-width: 20rem;
      align-items: center;

      @mixin screen lg {
        flex-direction: column;
        text-align: center;
      }
      header {
        width: 50%;
        .step-number {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 4rem;
          width: 4rem;
          background-color: black;
          border-radius: 100%;
          font-size: 1.5rem;
          color: white;
        }
      }
      main {
        padding: 1rem;
        padding-top: 2rem;

        @mixin screen lg {
          padding: 1rem;
          padding-top: 2rem;
        }

        h3 {
          margin: 0;
          font-weight: 500;
        }

        p {
          color: var(--color-gray-500);
        }
      }
    }
  }
}

.how-much-earn {
  margin: 1rem auto 0;
  padding: 6rem 2rem 4rem;
  max-width: var(--default-content-width);
  background: linear-gradient(292deg, rgba(254, 227, 229, 0.60) 22.32%, rgba(254, 227, 229, 0.00) 56.09%, rgba(254, 227, 229, 0.60) 87.17%);
  text-align: center;
  grid-template-columns: 1fr;
  @mixin screen sm {
    text-align: left;
    display: grid;
    padding-inline: 5rem;
  }
  .slider-container {
    @mixin screen sm {
      padding-right: 2rem;
    }
    .bubble {
      color: var(--color-orange-500);
      font-size: 2rem;
      margin-left: 0.5rem;
    }
    h4 {
      margin: 2rem 0 1rem;
    }
    p {
      margin: 0;
      @mixin screen sm {
        padding-right: 2rem;
      }
    }
  }

  @mixin screen sm {
    grid-template-columns: 1fr 1fr;
  }

  @mixin screen lg {
    grid-template-columns: 1fr 1fr 1fr;
    border-radius: 2rem;
  }
  @mixin screen xl {
    grid-template-columns: 1fr 1fr 1fr
  }
  header {
    grid-column: 1/1;
    margin-bottom: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;

    @mixin screen sm {
      align-items: flex-start;
      grid-column: 1/-1;
    }

    @mixin screen lg {
      grid-column: 1/3;
    }
    @mixin screen xl {
      grid-column: 1/3;
    }
  }

  .earnings-estimate {
    margin-top: 2rem;

    @mixin screen lg {
      margin-top: 0;
      grid-row: 1;
      grid-column: 3
    }

    @mixin screen xl {
      grid-row: 1;
      grid-column: 3
    }

    .title {
      background: var(--color-rose-100);
      padding: 0.5rem 1rem;
      border-radius: 2rem;
      color: var(--color-rose-500);
      font-size: 1rem;
      font-weight: 500;

    }

    .earnings-container {
      margin-top: 2rem;
      .currency {
        color: var(--color-rose-500);
        font-size: 2rem;
        font-weight: 600;
        position: relative;
        top: -2rem;
      }
      .value {
        color: var(--color-rose-500);
        font-size: 5rem;
        font-weight: 600;
      }
      .period {
        color: var(--color-rose-500);
        font-size: 2rem;
        font-weight: 400;
      }
    }

  }

  .disclaimer {
    width: 100%;
    grid-column: span 2;
    margin-top: 3rem;
    @mixin typeface-detail;
  }
}

.built-for-creators {
  margin: 3rem auto 0;
  @mixin section-gap;
  justify-content: center;
  display: grid;
  max-width: var(--default-content-width);
  position: relative;

  header {
    text-align: center;
    @mixin screen sm {
      text-align: left;
    }
  }

  main {
    display: grid;
    place-items: center;
    grid-template-columns: 1fr;
    position: relative;
    @mixin screen sm {
      grid-template-columns: 2fr 1fr;
    }

    .creator-card-container {
      padding-top: 3rem;
      display: grid;
      grid-template-columns: 1fr;
      max-width: 20rem;
      gap: 1rem;

      @mixin screen sm {
        grid-template-columns: 1fr 1fr;
        max-width: 100%;
      }

      @mixin screen lg {
        grid-template-columns: 1fr 1fr 1fr;
      }

      @mixin screen xl {
        grid-template-columns: 1fr 1fr 1fr;
      }

      .for-creators-card {
        display: grid;
        grid-template-columns: 2rem 1fr;
        justify-content: center;

        .content {
          h4 {
            margin: 0;
            padding: 0;
          }
          p {
            color: var(--color-gray-400);
          }
        }

        .icon {
          position: relative;
          top: 0;
          max-width: 1.5rem;
          opacity: 0.9;
          @mixin screen sm {
            top: 0.15rem;
          }
        }
      }
    }

    .skew-container {
      position: absolute;
      top: 0;
      right: 2000px;

      @mixin screen sm {
        top: -14rem;
        right: -200px;
      }
      @mixin screen lg {
        top: -14rem;
        right: -200px;
      }

      .outline {
        position: relative;
        top: 5.5rem;
        left: -6rem;
        z-index: 2;
      }
      .fill {
        position: absolute;
        width: 120%;
        top: 20px;
        left: -50px;
        z-index: 1;
      }
    }
  }
}

.made-with-mapper-carousel {
  margin: 3rem auto 0;
  margin-block: 5rem;
  text-align: center;
  display: grid;
  place-items: center;
  max-width: var(--default-content-width);
  main {
    width: 100%
  }

  .maps-carousel {
    display: flex;
    height: 24rem;
    gap: 1rem;
    max-width: 80rem;
    overflow: hidden;

    .map-carousel-item {
      min-width: 15rem;
      max-width: 15rem;
      border-radius: 0.5rem;
    }
  }
}

.creator-faq {
  margin: 0 auto;
  @mixin section-gap;
  text-align: center;
  display: grid;
  place-items: center;
  max-width: var(--default-content-width);

  .faq-list {
    max-width: 40rem;
  }
}
</style>
